// SubAgentBoxV2 — v2-styled sub-agent surface for the right-rail panel.
//
// Tracks the same lastToolEvent stream as v1 SubAgentBox (filtered by
// `subAgent.id`) so a sub-agent's todoList renders independently from
// the main agent's. Surfaces three collapse levels:
//   0  Title row only — name, status, percent. Compact.
//   1  Title row + TaskListV2 with the sub-agent's own tasks. Default
//      when used inside the RunProgressPanel (`compact={true}`).
//   2  Same as level 1 plus the inline message body — useful for
//      debugging mid-flight sub-agent runs.
//
// Visuals match the v2 design: rounded `bg0` card, soft border that
// glows amber while the sub-agent is running, uppercase title strip.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import type { SubAgentState } from '../AiChat/stateConfiguration';
import type { TodoList } from '../../models/todoListModel';
import { useAiChatSessionStore } from '../AiChat/AiChatContext';
import { TaskListV2 } from './TaskListV2';
import { ChevronIcon, BrainIcon } from './icons';

export type SubAgentBoxV2Level = 0 | 1 | 2;

const subagentScanlineKeyframes = `
@keyframes v2sascan {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}`;

export interface SubAgentBoxV2Props {
    subAgent: SubAgentState;
    /**
     * Initial collapse level. When omitted, defaults to `1` (compact —
     * matches the right-rail panel default).
     */
    defaultLevel?: SubAgentBoxV2Level;
    /** Suppress level 2 (inline messages) — useful when rendered inside the right rail. */
    hideMessageLevel?: boolean;
    onLinkClicked?: (link: string, content: string | null) => void;
}

function tryParse(json: string | undefined): TodoList | null {
    if (!json) return null;
    try {
        return JSON.parse(json) as TodoList;
    } catch {
        return null;
    }
}

export const SubAgentBoxV2: React.FC<SubAgentBoxV2Props> = ({
    subAgent,
    defaultLevel = 1,
    hideMessageLevel = true,
}) => {
    const [level, setLevel] = useState<SubAgentBoxV2Level>(defaultLevel);
    const [todoList, setTodoList] = useState<TodoList | null>(null);

    const lastToolEvent = useAiChatSessionStore((state) => state.lastToolEvent);

    const isRunning = !subAgent.completed;

    // Adopt todoList updates emitted for this sub-agent only.
    useEffect(() => {
        if (
            lastToolEvent?.eventName === 'todolistUpdated'
            && lastToolEvent.subAgentId === subAgent.id
        ) {
            const parsed = tryParse(lastToolEvent.eventData);
            if (parsed) setTodoList(parsed);
        }
    }, [lastToolEvent, subAgent.id]);

    const counts = useMemo(() => {
        const items = todoList?.items ?? [];
        const completed = items.filter((i) => String(i.status).toLowerCase() === 'completed').length;
        const total = items.length;
        const pct = total > 0 ? completed / total : 0;
        return { completed, total, pct };
    }, [todoList]);

    const cycle = useCallback(() => {
        setLevel((prev) => {
            const max: SubAgentBoxV2Level = hideMessageLevel ? 1 : 2;
            const next = prev >= max ? 0 : ((prev + 1) as SubAgentBoxV2Level);
            return next;
        });
    }, [hideMessageLevel]);

    const showTasks = level >= 1 && (todoList?.items.length ?? 0) > 0;
    const showMessages = level >= 2 && !hideMessageLevel;

    return (
        <Box
            sx={(theme) => ({
                position: 'relative',
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${isRunning ? theme.palette.primary.dark : theme.palette.border.soft}`,
                borderRadius: `${theme.shape.borderRadius}px`,
                overflow: 'hidden',
                transition: 'border-color .15s',
            })}
        >
            <style>{subagentScanlineKeyframes}</style>

            {/* "Working" scanline at the top edge while running. */}
            {isRunning && (
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={(theme) => ({
                            width: '100%',
                            height: '100%',
                            background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                            animation: 'v2sascan 2s ease-in-out infinite',
                        })}
                    />
                </Box>
            )}

            {/* Header — name + status + percent + cycle toggle. */}
            <Box
                role="button"
                tabIndex={0}
                onClick={cycle}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cycle(); } }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <Box
                    sx={(theme) => ({
                        width: 18,
                        height: 18,
                        flexShrink: 0,
                        color: isRunning ? theme.palette.primary.main : theme.palette.success.main,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    })}
                >
                    {isRunning ? <BrainIcon size={14} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                </Box>

                <Typography
                    sx={(theme) => ({
                        fontSize: 12,
                        fontWeight: 500,
                        color: isRunning ? theme.palette.text.primary : theme.palette.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                    })}
                >
                    {subAgent.name ?? subAgent.id}
                </Typography>

                {counts.total > 0 && (
                    <Typography
                        sx={(theme) => ({
                            fontSize: 10.5,
                            color: theme.palette.text.disabled,
                            fontFamily: theme.typography.fontFamilyMono,
                            flexShrink: 0,
                        })}
                    >
                        {counts.completed}/{counts.total}
                    </Typography>
                )}

                <Tooltip title={level === 0 ? 'Mostrar tarefas' : 'Recolher'}>
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); cycle(); }}
                        sx={(theme) => ({
                            color: theme.palette.text.secondary,
                            p: 0.25,
                            transform: level === 0 ? 'rotate(-90deg)' : 'none',
                            transition: 'transform .15s',
                        })}
                    >
                        <ChevronIcon size={14} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Progress bar — always rendered (even at level 0) so the
                sub-agent's percent is visible at a glance. */}
            {counts.total > 0 && (
                <Box
                    sx={(theme) => ({
                        height: 2,
                        backgroundColor: theme.palette.surface.raised,
                        overflow: 'hidden',
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            width: `${Math.round(counts.pct * 100)}%`,
                            height: '100%',
                            backgroundColor: theme.palette.primary.main,
                            transition: 'width .25s ease',
                        })}
                    />
                </Box>
            )}

            {/* Body — tasks (level >=1) and messages placeholder (level 2). */}
            {showTasks && (
                <Box sx={{ px: 1, pt: 0.75, pb: 0.5 }}>
                    <TaskListV2 todoList={todoList} dense />
                </Box>
            )}
            {showMessages && (
                <Box sx={{ px: 1.5, pb: 1, pt: 0.5 }}>
                    <Typography
                        sx={(theme) => ({
                            fontSize: 10.5,
                            color: theme.palette.text.disabled,
                            fontStyle: 'italic',
                            fontFamily: theme.typography.fontFamilyMono,
                        })}
                    >
                        Logs detalhados deste sub-agente em breve.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

SubAgentBoxV2.displayName = 'SubAgentBoxV2';
